import moment from 'moment';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ENTITY_REQUEST_ANSWER_TYPES } from '../../types/entity_add_requests';

// Identifies the exact contact values a duplicate lookup was last run for, so the same search isn't
// re-issued and an already-answered "did you mean" prompt isn't shown twice.
export const getDuplicateSearchKey = (values) =>
	`${nullSafeGet('companyName', values)}|${nullSafeGet('contactEmail', values)}`;

// Map a supplier facility returned by the duplicate lookup onto the contact fields of the supplier
// request form, so picking a match fills the request with that supplier's details.
export const getSupplierMatchContactDetails = (supplier) => ({
	companyName: nullSafeGet('displayName', supplier) || nullSafeGet('name', supplier),
	contactEmail:
		nullSafeGet('primaryContactEmail', supplier) || nullSafeGet('primaryContact.email', supplier),
	contactFirstName: nullSafeGet('primaryContact.nameGiven', supplier),
	contactLastName: nullSafeGet('primaryContact.nameFamily', supplier),
});

// Build the supplier `payload` from AntD form values. "Select All" means no id constraint, so
// the ids array is emptied when the corresponding toggle is on.
export const buildSupplierPayload = (values) => {
	const allProblemTypes = !!values.allProblemTypes;
	const allLocations = !!values.allLocations;
	return {
		companyName: values.companyName,
		contactEmail: values.contactEmail,
		contactFirstName: values.contactFirstName,
		contactLastName: values.contactLastName,
		allProblemTypes,
		problemTypeIds: allProblemTypes ? [] : nullSafeGetOrElse('problemTypeIds', values, []),
		allLocations,
		locationIds: allLocations ? [] : nullSafeGetOrElse('locationIds', values, []),
	};
};

const serializeAnswer = (question, rawAnswer) => {
	if (rawAnswer === undefined || rawAnswer === null) {
		return rawAnswer;
	}
	if (question.answerType === ENTITY_REQUEST_ANSWER_TYPES.date && moment.isMoment(rawAnswer)) {
		return rawAnswer.format('YYYY-MM-DD');
	}
	return rawAnswer;
};

// Rebuild the [{ question, answer, orderingId }] answer list from the nested `answers` form value.
export const buildAnswers = (questions, values) => {
	const answersByOrderingId = nullSafeGetOrElse('answers', values, {});
	return (questions || []).map((question) => ({
		question,
		answer: serializeAnswer(question, answersByOrderingId[String(question.orderingId)]),
		orderingId: question.orderingId,
	}));
};

const deserializeAnswer = (question, storedAnswer) => {
	if (storedAnswer === undefined || storedAnswer === null) {
		return storedAnswer;
	}
	if (question.answerType === ENTITY_REQUEST_ANSWER_TYPES.date) {
		return moment(storedAnswer);
	}
	return storedAnswer;
};

// Produce AntD form initialValues from an existing request payload + its answers, keyed by
// question orderingId so they line up with EntityRequestAnswerFields.
export const getSupplierRequestInitialValues = (record, questions) => {
	const payload = nullSafeGetOrElse('payload', record, {});
	const storedAnswers = nullSafeGetOrElse('answers', record, []);
	const answersByOrderingId = {};
	storedAnswers.forEach((answerEntry) => {
		const orderingId = nullSafeGet('orderingId', answerEntry);
		if (orderingId !== undefined && orderingId !== null) {
			answersByOrderingId[String(orderingId)] = answerEntry.answer;
		}
	});

	const answers = {};
	(questions || []).forEach((question) => {
		const key = String(question.orderingId);
		answers[key] = deserializeAnswer(question, answersByOrderingId[key]);
	});

	const problemTypeIds = nullSafeGetOrElse('problemTypeIds', payload, []);
	const locationIds = nullSafeGetOrElse('locationIds', payload, []);

	return {
		companyName: nullSafeGet('companyName', payload),
		contactEmail: nullSafeGet('contactEmail', payload),
		contactFirstName: nullSafeGet('contactFirstName', payload),
		contactLastName: nullSafeGet('contactLastName', payload),
		problemTypeIds,
		locationIds,
		allProblemTypes: nullSafeGetOrElse('allProblemTypes', payload, problemTypeIds.length === 0),
		allLocations: nullSafeGetOrElse('allLocations', payload, locationIds.length === 0),
		answers,
	};
};
